import React from "react";
import NotificationItem from "../notification-item/NotificationItem";
import { Divider, Stack, Typography } from "@mui/material";
import SingleUserIcon from "src/icons/SingleUserIcon";
import CalendarIcon from "src/icons/CalendarIcon";
import { useTheme } from "@mui/material/styles";
import { STEP_CONTENT, STEP_STATUS_DISPLAY_TEXT } from "./NotificationDetailedView.consts";
import { getStepVisuals, formatDate } from "./NotificationDetailedView.helpers";
import { NOTIFICATION_STATUSES } from "../notifications.consts";
import ErrorMessageCollapsible from "../error-message-collapsible/ErrorMessageCollapsible";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "src/stores/appStore";
import {
  MetaInfoContainer,
  InfoRow,
  StepsContainer,
  StepRow,
  VisualStepContainer,
  IconContainer,
  StepConnectorLine,
  SuccessContainer,
  SuccessText
} from "./NotificationDetailedView.styles";
import NotificationErrorBox from "../notification-error-box/NotificationErrorBox";
import NotificationConfigIdBox from "../notification-config-id-box/NotificationConfigIdBox";
import { isValidObjectId } from "@utils";

const NotificationDetailedView = ({ notification }) => {
  const theme = useTheme();
  const hasPermission = useAppStore(hasPermissionSelector);
  const canReadError = hasPermission(USER_PERMISSIONS.NOTIFICATIONS.ERROR.READ);

  return (
    <>
      <NotificationItem {...notification} isExpanded={true} />
      <Divider />

      <MetaInfoContainer>
        <InfoRow>
          <SingleUserIcon />
          <Typography variant="chip" component="span" data-testid="initiated-by">
            Initiated by
            <Typography variant="caption" component="span"> {notification.createdBy}</Typography>
          </Typography>
        </InfoRow>

        <InfoRow>
          <CalendarIcon size={16} color={theme.palette.text[700]} strokeWidth={1.2} />
          <Typography variant="chip" component="span" data-testid="started-on">
            Started on
            <Typography variant="caption" component="span"> {formatDate(notification.createdAt)}</Typography>
          </Typography>
        </InfoRow>
      </MetaInfoContainer>

      <Typography variant="tableHeader1" data-testid="steps-title">{STEP_CONTENT[notification.type].title}</Typography>

      <StepsContainer>
        {notification.steps.map((step, index) => {
          const visuals = getStepVisuals(notification.status, step.status);
          const isLastStep = index === notification.steps.length - 1;
          return (
            <StepRow key={step.key} isLastStep={isLastStep}>
              <VisualStepContainer>
                <IconContainer>{visuals.icon}</IconContainer>
                {!isLastStep && <StepConnectorLine lineColor={visuals.lineColor} />}
              </VisualStepContainer>
              <Stack>
                <Typography variant="subtitle2">{STEP_CONTENT[notification.type].steps[index]}</Typography>
                <Typography variant="caption" component="div" color={visuals.textColor} data-testid={`step-status-${step.key}`}>{STEP_STATUS_DISPLAY_TEXT[step.status]}</Typography>
              </Stack>
            </StepRow>
          );
        })}
      </StepsContainer>

      {notification.status === NOTIFICATION_STATUSES.COMPLETED && <SuccessContainer>
        <SuccessText variant="chip" data-testid="success-message">
          {STEP_CONTENT[notification.type].successMessage} {formatDate(notification.lastUpdate)}
        </SuccessText>
      </SuccessContainer>}

      {notification.status === NOTIFICATION_STATUSES.FAILED && notification.errorId && (
        <NotificationErrorBox notificationType={notification.type} errorId={notification.errorId} />
      )}

      {isValidObjectId(notification.configId) && canReadError && (
        <NotificationConfigIdBox configId={notification.configId} />
      )}

      {notification.errorDescription && canReadError && (
        <ErrorMessageCollapsible errorMessage={notification.errorDescription} data-testid="error-message" />
      )}
    </>
  );
};

export default NotificationDetailedView;
